import { communityDao } from '../dao/community-dao'
import {
  AdminCommentListParams,
  AdminCommentsListResponse,
  CommunityComment,
  CommunityCommentsResponse,
  CommunityHustler,
  CommunityRepliesResponse,
  CreateCommentPayload,
  ReactionPayload,
  ReactionSummary,
  ReactionTypeDefinition,
  UpdateAdminCommentPayload,
} from '../types/community'

const mapAnonymousHustler = (hustler?: CommunityHustler): CommunityHustler => {
  if (hustler) return hustler
  return {
    id: 'unknown',
    name: 'Deleted user',
  }
}

const hydrateHustlers = (comments: CommunityComment[]): CommunityComment[] => {
  return comments.map((comment) => ({
    ...comment,
    hustler: mapAnonymousHustler(comment.hustler),
    replies: comment.replies?.map((reply) => ({
      ...reply,
      hustler: mapAnonymousHustler(reply.hustler),
    })),
  }))
}

export const communityService = {
  async listComments(params: {
    entityType: string
    entityId: string
    page?: number
    pageSize?: number
    sortBy?: string
  }): Promise<CommunityCommentsResponse> {
    const response = await communityDao.listComments(params)
    return {
      ...response,
      data: hydrateHustlers(response.data),
    }
  },

  async listReplies(params: { commentId: string; page?: number; pageSize?: number }): Promise<CommunityRepliesResponse> {
    const response = await communityDao.listReplies(params)
    return {
      ...response,
      data: response.data.map((reply) => ({
        ...reply,
        hustler: mapAnonymousHustler(reply.hustler),
      })),
    }
  },

  async createComment(payload: CreateCommentPayload): Promise<CommunityComment> {
    const created = await communityDao.createComment(payload)
    return {
      ...created,
      hustler: mapAnonymousHustler(created.hustler),
    }
  },

  async deleteComment(commentId: string): Promise<void> {
    return communityDao.deleteComment(commentId)
  },

  async getReactions(params: { entityType: string; entityId: string }): Promise<ReactionSummary> {
    return communityDao.getReactions(params)
  },

  async toggleReaction(payload: ReactionPayload): Promise<ReactionSummary> {
    return communityDao.toggleReaction(payload)
  },

  async getReactionTypes(): Promise<ReactionTypeDefinition[]> {
    return communityDao.getReactionTypes()
  },

  async adminListComments(params: AdminCommentListParams): Promise<AdminCommentsListResponse> {
    const response = await communityDao.adminListComments(params)
    return { ...response, data: hydrateHustlers(response.data) }
  },

  async adminGetComment(commentId: string): Promise<CommunityComment> {
    const comment = await communityDao.adminGetComment(commentId)
    return { ...comment, hustler: mapAnonymousHustler(comment.hustler) }
  },

  async adminUpdateComment(commentId: string, payload: UpdateAdminCommentPayload): Promise<CommunityComment> {
    const comment = await communityDao.adminUpdateComment(commentId, payload)
    return { ...comment, hustler: mapAnonymousHustler(comment.hustler) }
  },

  async adminDeleteComment(commentId: string): Promise<void> {
    return communityDao.adminDeleteComment(commentId)
  },
}
